import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { PencilLine, Search, Users } from "lucide-react";
import { toast } from "react-toastify";

import TableComponent from "../components/common/TableComponent";
import PaginationComponent from "../components/PaginationComponent";
import Loading from "../components/common/LoaderCommon";
import Modal from "../components/common/Modal";
import AddNewUser from "../components/forms/AddNewUser";
import { useInfiniteGetAllUsers } from "../service/userService";
import useUserRequestStore from "../store/useUserStore";

function User() {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchKey, setSearchKey] = useState("");

  const { setCurrentUser } = useUserRequestStore();

  const { data, fetchNextPage, isLoading } = useInfiniteGetAllUsers({
    pageSize: 10,
    pageNo: currentPage,
    userName: searchKey,
  });

  const totalPages = Math.ceil((data?.pages[0]?.totalPages || 0) / 10);
  const userData = data?.pages[0]?.userDetailsResponse || [];

  const handleEdit = (row: any) => {
    const rowData = row.original;
    if (!rowData) {
      toast.error("Error: No user data available");
      return;
    }
    setCurrentUser({
      id: rowData.id,
      username: rowData.username || "",
      email: rowData.email || "",
      phoneNumber: rowData.phoneNumber || "",
      role: rowData.role || "",
      address: rowData.address || "",
      password: "",
    });
    setTimeout(() => setIsModalOpen(true), 50);
  };

  const handleAddNewUser = () => {
    setCurrentUser(null);
    setIsModalOpen(true);
  };

  const handleModalClose = (open: boolean) => {
    setIsModalOpen(open);
    if (!open) setCurrentUser(null);
  };

  const usersTColumns = [
    { header: "Name", accessor: "username" },
    { header: "Email", accessor: "email" },
    { header: "Role", accessor: "role" },
    { header: "Address", accessor: "address" },
    { header: "Phone Number", accessor: "phoneNumber" },
    {
      header: "Action",
      accessor: "action",
      className: "text-center",
      cell: ({ row }: any) => (
        <div className="flex justify-center">
          <button
            onClick={() => handleEdit(row)}
            className="rounded-[10px] border border-[#D8E4FB] p-2 text-[#6B7C98] transition hover:border-[#B9CEFF] hover:text-[#1A5EF9]"
            title="Edit user"
            aria-label="Edit user"
          >
            <PencilLine className="w-4 h-4" />
            <span className="sr-only">Edit</span>
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page header card */}
      <div className="rounded-[26px] border border-[#E8EEF9] bg-white px-6 py-6 shadow-[0_16px_40px_rgba(26,94,249,0.08)]">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-[12px] bg-[#EEF3FF] flex items-center justify-center">
              <Users className="w-5 h-5 text-[#1A5EF9]" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-[#1A5EF9]">
                User Management
              </h1>
              <p className="text-sm text-[#7D8998] mt-0.5">
                Add and manage staff accounts and their roles
              </p>
            </div>
          </div>
          <button
            onClick={handleAddNewUser}
            className="h-11 rounded-[12px] bg-[#1A5EF9] px-6 text-white font-medium text-sm hover:bg-[#0E4FDE] transition-colors"
          >
            Add New User
          </button>
        </div>
      </div>

      {/* Search + Table card */}
      <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)]">
        {/* Search bar */}
        <div className="px-6 py-4 border-b border-[#E8EEF9]">
          <div className="relative max-w-md">
            <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#91A0B8]" />
            <input
              type="text"
              placeholder="Search by name..."
              onChange={(e) => {
                setSearchKey(e.target.value);
                setCurrentPage(0);
              }}
              className="h-12 w-full rounded-[14px] border border-[#E5ECF8] bg-[#F5F8FD] pl-11 pr-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
            />
          </div>
        </div>

        {/* Table header with count + pagination */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-[#E8EEF9]">
          <p className="text-sm text-[#52627C]">
            <span className="font-semibold text-[#21314A]">
              {userData?.length || 0}
            </span>{" "}
            users found
          </p>
          <PaginationComponent
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="flex min-h-[200px] items-center justify-center">
            <Loading />
          </div>
        ) : (
          <TableComponent
            caption="A list of your users."
            columns={usersTColumns}
            data={userData || []}
          />
        )}
      </div>

      {isModalOpen && (
        <Modal open={isModalOpen} setOpen={handleModalClose}>
          <AddNewUser setOpen={handleModalClose} />
        </Modal>
      )}
    </div>
  );
}

export default User;
